import { Link } from "react-router-dom";
import {
  Phone,
  ArrowRight,
  Stethoscope,
  HeartPulse,
  Sparkles,
  Coffee,
  ShieldCheck,
  Users,
  Award,
  Clock,
  Heart,
  MapPin,
  CalendarCheck,
  HandHeart,
  Star,
  Wallet,
  BadgeCheck,
  GraduationCap,
} from "lucide-react";
import SEO from "../components/SEO";
import TrustBadges from "../components/TrustBadges";
import LeadForm from "../components/LeadForm";
import { Button } from "../components/ui/button";
import { COMPANY, IMAGES, OFFICE_HOURS } from "../lib/constants";

const services = [
  {
    icon: Stethoscope,
    title: "Skilled Nursing",
    desc: "RN-led medication management, wound care, and post-hospital recovery in the comfort of home.",
    img: IMAGES.skilledNursing,
  },
  {
    icon: HeartPulse,
    title: "Private Duty Nursing",
    desc: "24/7 one-on-one care for ventilator, trach, pediatric, and adult complex care needs.",
    img: IMAGES.heroNurse,
  },
  {
    icon: Sparkles,
    title: "Personal Care",
    desc: "Bathing, grooming, mobility, meal prep, and friendly companionship — every day.",
    img: IMAGES.personalCare,
  },
  {
    icon: Coffee,
    title: "Respite Care",
    desc: "Short-term, flexible relief for family caregivers — so you can rest, knowing your loved one is safe.",
    img: IMAGES.respiteCare,
  },
];

const whyUs = [
  { icon: ShieldCheck, title: "Licensed & Medicaid-Approved", desc: "Fully accredited Virginia home care organization." },
  { icon: Users, title: "Experienced RN Leadership", desc: "Led by an experienced Registered Nurse team." },
  { icon: Heart, title: "Personalized Care Plans", desc: "Plans built around each family's unique needs and goals." },
  { icon: Clock, title: "Fast 24–48 Hour Onboarding", desc: "Same-day intake. Care can begin within 24–48 hours." },
  { icon: Award, title: "Compassionate Caregivers", desc: "Vetted, trained, and matched to your loved one." },
  { icon: MapPin, title: "Trusted Across Northern VA", desc: "4 counties + 5 cities across Northern Virginia." },
];

export default function Home() {
  return (
    <>
      <SEO
        title="1st Home Care Services | Home Care in Springfield, Fairfax & Northern Virginia"
        description="Compassionate, licensed home care services in Springfield, Fairfax, and Northern Virginia. Skilled nursing, private duty, personal care, and respite care. Medicaid approved."
        keywords="home care services Springfield VA, home health care Fairfax VA, Medicaid home care Virginia, private duty nursing Northern Virginia, skilled nursing at home VA"
        canonical="https://1sthomecare.com/"
      />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-gradient-to-b from-white via-blue-50/40 to-white"
      >
        <div className="container-pad section-pad grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-brand-amber/10 border border-brand-amber/30 text-brand-amber-dark text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
              <Heart className="w-3.5 h-3.5" /> Serving Northern Virginia since day one
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-blue-dark text-balance">
              Care You Can Trust.
              <span className="block text-brand-amber"> Comfort You Deserve.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl text-pretty">
              Compassionate, licensed home care services in Springfield, Fairfax,
              and Northern Virginia. From skilled nursing to companionship —
              we bring hospital-grade care home.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="hero-get-care"
                className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold px-7 py-4 rounded-full shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                Get Care Now <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneTel}`}
                data-testid="hero-call-now"
                className="inline-flex items-center gap-2 bg-brand-amber hover:bg-brand-amber-dark text-white font-semibold px-7 py-4 rounded-full shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                <Phone className="w-4 h-4" /> Call {COMPANY.phone}
              </a>
            </div>

            <div className="mt-8">
              <TrustBadges variant="left" />
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white">
              <img
                src={IMAGES.heroNurse}
                alt="Compassionate caregiver with elderly patient at home"
                className="w-full h-[440px] md:h-[540px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-dark/30 via-transparent to-transparent" />
            </div>

            <div className="hidden md:flex absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 gap-4 items-center border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <div className="text-xs text-gray-500 font-medium">Care can start in</div>
                <div className="font-heading font-bold text-brand-blue-dark text-lg">24–48 hours</div>
              </div>
            </div>

            <div className="hidden md:flex absolute -top-5 -right-5 bg-brand-amber text-white rounded-2xl shadow-xl px-4 py-3 gap-2 items-center">
              <Phone className="w-5 h-5" />
              <div>
                <div className="text-xs opacity-90">Speak with a nurse</div>
                <a href={`tel:${COMPANY.phoneTel}`} className="font-bold tracking-wide">
                  {COMPANY.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section data-testid="intro-section" className="container-pad py-12 md:py-16">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-base md:text-lg text-gray-700 leading-relaxed text-pretty">
            <span className="font-semibold text-brand-blue-dark">1st Home Care Services</span> is
            a licensed, compassionate home care provider proudly serving families across
            Northern Virginia. Our team of registered nurses and dedicated caregivers
            delivers personalized, family-centered care — so your loved ones can stay safe,
            independent, and comfortable in the place they love most: home.
          </p>
        </div>
      </section>

      {/* QUICK ACTIONS — Appointment / Referral / Survey */}
      <section data-testid="quick-actions" className="container-pad pb-12 md:pb-16">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { to: "/contact", icon: CalendarCheck, title: "Set an Appointment", desc: "Discuss your needs and develop the ideal care plan.", cta: "Schedule Now" },
            { to: "/referral", icon: HandHeart, title: "Send Your Referrals", desc: "Lend someone you know a hand to receive our care.", cta: "Refer Someone" },
            { to: "/survey", icon: Star, title: "Client Satisfaction Survey", desc: "Let us know how well we have served your needs.", cta: "Share Feedback" },
          ].map((c) => (
            <Link
              key={c.title}
              to={c.to}
              data-testid={`quick-action-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 p-6 flex gap-4 items-start"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-amber/10 text-brand-amber-dark flex items-center justify-center shrink-0 group-hover:bg-brand-amber group-hover:text-white transition-colors">
                <c.icon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="font-heading text-lg font-semibold text-brand-blue-dark">{c.title}</div>
                <div className="text-sm text-gray-600 mt-1">{c.desc}</div>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue group-hover:text-brand-amber-dark transition-colors">
                  {c.cta} <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section data-testid="services-preview" className="bg-brand-gray/50 section-pad">
        <div className="container-pad">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
              Our Services
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-blue-dark tracking-tight">
              Home care, tailored to every need
            </h2>
            <p className="mt-3 text-gray-600">
              From routine personal care to complex skilled nursing — choose the level of
              support that's right for your family.
            </p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s) => (
              <article
                key={s.title}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center mb-4">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-brand-blue-dark mb-2">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-brand-blue hover:text-brand-amber transition-colors"
                  >
                    Learn More <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section data-testid="why-choose-us" className="container-pad section-pad">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
              Why Families Choose Us
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-blue-dark tracking-tight">
              Hospital-grade care. Hometown compassion.
            </h2>
            <p className="mt-4 text-gray-600 leading-relaxed">
              We blend clinical excellence with genuine warmth, so every family
              feels seen, supported, and never alone.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              {whyUs.map((w) => (
                <div
                  key={w.title}
                  data-testid={`why-card-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex gap-3 p-4 rounded-xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-10 h-10 rounded-lg bg-brand-amber/10 text-brand-amber-dark flex items-center justify-center shrink-0">
                    <w.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-brand-blue-dark">{w.title}</div>
                    <div className="text-sm text-gray-600 mt-0.5">{w.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img
              src={IMAGES.elderlyCouple}
              alt="Elderly couple at home receiving compassionate home care"
              className="rounded-3xl shadow-xl w-full h-[520px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-brand-blue text-white rounded-2xl p-5 shadow-xl max-w-[260px]">
              <div className="font-heading text-2xl font-bold">100%</div>
              <div className="text-sm opacity-90 leading-snug">
                Personalized care plans, built by a registered nurse.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section
        id="get-care"
        data-testid="home-lead-form-section"
        className="bg-gradient-to-br from-brand-blue-dark via-brand-blue to-brand-blue-light text-white section-pad relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10 grain-overlay" aria-hidden />
        <div className="container-pad relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber-light mb-3">
              Get Started Today
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Speak with a nurse today.
              <span className="block text-brand-amber-light">
                Care can begin within 24–48 hours.
              </span>
            </h2>
            <p className="mt-5 text-white/80 leading-relaxed max-w-lg">
              Tell us a little about your situation. A care coordinator will follow up
              quickly to schedule a free in-home assessment.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`tel:${COMPANY.phoneTel}`}
                data-testid="lead-section-call-now"
                className="inline-flex items-center gap-2 bg-brand-amber hover:bg-brand-amber-dark text-white font-semibold px-6 py-3.5 rounded-full"
              >
                <Phone className="w-4 h-4" /> Call {COMPANY.phone}
              </a>
              <a
                href={`mailto:${COMPANY.primaryEmail}`}
                data-testid="lead-section-email"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3.5 rounded-full backdrop-blur"
              >
                Email Us
              </a>
            </div>
          </div>
          <div>
            <LeadForm />
          </div>
        </div>
      </section>

      {/* PAYMENT + HOURS */}
      <section data-testid="payment-hours-section" className="container-pad section-pad">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Payment */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-10">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
              Payment Options
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-brand-blue-dark tracking-tight">
              Flexible payment for every family.
            </h2>
            <p className="mt-3 text-gray-600 text-sm leading-relaxed">
              We accept Medicaid and offer private pay. Our team helps you understand
              your coverage and start care quickly.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <div className="bg-brand-gray/60 rounded-xl p-5 flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-blue text-white flex items-center justify-center shrink-0">
                  <BadgeCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-brand-blue-dark">Medicaid Accepted</div>
                  <div className="text-xs text-gray-600 mt-0.5">Approved VA Medicaid provider</div>
                </div>
              </div>
              <div className="bg-brand-gray/60 rounded-xl p-5 flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-amber text-white flex items-center justify-center shrink-0">
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-heading font-semibold text-brand-blue-dark">Private Pay Available</div>
                  <div className="text-xs text-gray-600 mt-0.5">Flexible plans, transparent pricing</div>
                </div>
              </div>
            </div>

            <Link
              to="/contact/payment"
              data-testid="home-payment-learn-more"
              className="inline-flex items-center gap-1 mt-6 text-sm font-semibold text-brand-blue hover:text-brand-amber-dark transition-colors"
            >
              Learn more about payment options <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Hours */}
          <div
            data-testid="home-hours-section"
            className="bg-brand-blue-dark text-white rounded-3xl p-8 md:p-10 shadow-sm relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10 grain-overlay" aria-hidden />
            <div className="relative">
              <div className="text-xs font-bold uppercase tracking-widest text-brand-amber-light mb-3">
                Office Hours
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
                We're here when you need us.
              </h2>
              <p className="mt-3 text-white/80 text-sm leading-relaxed">
                Office open weekdays. Weekends on call — care is available 24/7.
              </p>

              <ul className="mt-6 divide-y divide-white/10 border-y border-white/10">
                {OFFICE_HOURS.map((h) => (
                  <li
                    key={h.day}
                    data-testid={`hours-row-${h.day.toLowerCase()}`}
                    className="flex items-center justify-between py-2.5 text-sm"
                  >
                    <span className="font-semibold uppercase tracking-wider text-white/90 w-16">{h.day}</span>
                    <span className="text-white/85">{h.time}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-2 text-xs font-medium">
                <Clock className="w-4 h-4 text-brand-amber-light" />
                24/7 care available with same-day intake
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="cta-section" className="container-pad section-pad text-center">
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-blue-dark tracking-tight max-w-2xl mx-auto">
          Speak with a nurse today. Call now or request care online.
        </h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={`tel:${COMPANY.phoneTel}`}
            data-testid="bottom-call-now"
            className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold px-7 py-4 rounded-full shadow-md"
          >
            <Phone className="w-4 h-4" /> {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="bottom-request-care"
            className="inline-flex items-center gap-2 bg-brand-amber hover:bg-brand-amber-dark text-white font-semibold px-7 py-4 rounded-full shadow-md"
          >
            Request Care Online <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
